import { useCallback, useEffect, useRef, useState } from 'react';
import { useLogout } from '../../../lib/use-logout';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useTranslation } from '../../../lib/i18n';
import { SETTINGS_PAGE } from './settings-shared';
import { ApiError, useDeleteRecipient } from './settings-queries';

export interface DeleteAccount {
  visible: boolean;
  confirmText: string;
  setConfirmText: (v: string) => void;
  busy: boolean;
  error: string | null;
  canDelete: boolean;
  showConfirm: () => void;
  close: () => void;
  submit: () => Promise<void>;
}

/** The word the confirm asks for, untranslated so every locale types the same characters. */
export const DELETE_CONFIRMATION_PHRASE = 'DELETE';

/** Case-insensitive: a phone keyboard capitalizes the first letter and nothing else, and a gate
 *  nobody can satisfy on a phone is a broken screen, not a safer one. */
export function isDeleteConfirmed(typed: string): boolean {
  return typed.trim().toUpperCase() === DELETE_CONFIRMATION_PHRASE;
}

// Shared delete-account flow. Deletes the recipient, which cascades server-side to
// the metasite and all ISA data (mirrors mobile). The server resolves the target
// from the session, never the client, so it can only ever delete the caller's own
// account; the typed phrase guards only against a mis-tap on a row that sits one line
// below "Log out". Each surface renders its own confirmation modal off this state.
export function useDeleteAccount(): DeleteAccount {
  const { t } = useTranslation();
  const { logout } = useLogout();
  const { logPageClickBi, logModalActionBi } = useGenericBi();
  const deleteRecipient = useDeleteRecipient();
  const [visible, setVisible] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const deletingRef = useRef(false);

  const canDelete = isDeleteConfirmed(confirmText);

  const showConfirm = useCallback(() => {
    logPageClickBi({ ...SETTINGS_PAGE, button_name: 'delete_account' });
    setConfirmText('');
    setError(null);
    setVisible(true);
  }, [logPageClickBi]);

  const close = useCallback(() => {
    if (busy) return;
    logModalActionBi({ page_name: 'settings_page', modal_name: 'delete_account', action: 'cancel' });
    setVisible(false);
  }, [busy, logModalActionBi]);

  const submit = useCallback(async () => {
    if (!canDelete || deletingRef.current) return;
    deletingRef.current = true;
    logModalActionBi({ page_name: 'settings_page', modal_name: 'delete_account', action: 'confirm' });
    setBusy(true);
    setError(null);
    try {
      await deleteRecipient.mutateAsync();
      logPageClickBi({ ...SETTINGS_PAGE, button_name: 'delete_account_success' });
      logout();
      return;
    } catch (e) {
      const notFound = e instanceof ApiError && e.status === 404;
      setError(t(notFound ? 'settings.deleteAccount.error.notFound' : 'settings.deleteAccount.error.generic'));
    }
    setBusy(false);
    deletingRef.current = false;
  }, [canDelete, deleteRecipient, logModalActionBi, logPageClickBi, logout, t]);

  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, close]);

  return { visible, confirmText, setConfirmText, busy, error, canDelete, showConfirm, close, submit };
}
